import React, { useState, useEffect, useMemo } from "react";
import { useDebounce } from "use-debounce";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  Trash2,
  Plus,
  Minus,
  Info,
  Check,
  ShoppingCart,
  Loader2,
  MapPin,
  Tag,
  Crown,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@/components/ui/popover";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { useCart, ShippingOption, AppliedDiscount } from "@/context/CartContext";
import { useShipping, ShippingTier } from "@/context/ShippingContext";
import { useToast } from "@/hooks/use-toast";
import { useAuthContext } from "@/lib/AuthProvider";
import { useCurrency } from "@/context/CurrencyContext";
import { useUserProfile } from "@/context/UserProfileContext";
import AuthModalWrapper from "@/components/AuthModalWrapper";
import {
  checkProductsAvailability,
  fetchProductById,
} from "@/services/productService";
import { getActiveRushdTreats, RushdTreat } from "@/services/rushdTreatService";
import { CartRecommendations } from "@/components/CartRecommendations";
import { RewardProgressBar } from "@/components/RewardProgressBar";
import { validateDiscountCode, validateDiscountCodeForCart, DiscountCode } from "@/services/discountService";
import { formatDeliveryTime, getRushdPricingConfig, calculateRushdMemberRate, getExpressShippingDiscountPercent, applyShippingDiscount } from "@/services/shippingService";
import ScrollDownFAB from "@/components/ScrollDownFAB";
import ShippingAddressModal from "@/components/ShippingAddressModal";
import { useShippingAddress } from "@/context/ShippingAddressContext";
import { getCartSEO, validateSEOData, generateKeywords } from "@/services/seoService";
import { useRushdMembership, useRushdMembershipStatus } from "@/context/RushdMembershipContext";
import { useRushdSettings, getRushdPricingConfig as getRushdConfigFromSettings } from "@/context/RushdSettingsContext";
import { RushdPricingQuote } from "@/types/rushd";

const Cart = () => {
  const {
    cartItems,
    removeFromCart,
    updateQuantity,
    clearCart,
    getCartTotal,
    getCartCount,
    shippingOption,
    setShippingOption,
    getShippingCostSync,
    priorityProcessing,
    setPriorityProcessing,
    getPriorityProcessingFee,
    availableShippingTiers,
    loadingShippingTiers,
    appliedDiscount,
    setAppliedDiscount,
    getFinalShippingCost,
    getFinalTotal,
    getDiscountAmount,
    discountLoaded,
    applyDiscountToCartItems,
    getCartTotalWithDiscounts,
    setApplyingDiscount,
    syncRushdTreatItems,
  } = useCart();

  const { 
    getActiveShippingTiers, 
    getAllActiveShippingTiers,
    getAvailableShippingTiers,
    isTierAvailableInCountry,
    getShippingTierById,
    dhlExpressRate, 
    loadingDhlRate, 
    dhlRateError, 
    fetchDhlExpressRate 
  } = useShipping();

  const navigate = useNavigate();
  const { toast } = useToast();
  const { user } = useAuthContext();
  const { selectedCurrency, getConvertedItemPrice, formatDisplayAmount } = useCurrency();
  const { shippingAddress, setShippingAddress, isAddressComplete } = useShippingAddress();
  const { userProfile, loading: loadingProfile } = useUserProfile();
  const { getExpressPricingQuote } = useRushdMembership();
  const { isMember, isActive } = useRushdMembershipStatus();
  const { settings: rushdSettings, loading: rushdSettingsLoading } = useRushdSettings();
  
  const [promoCode, setPromoCode] = useState("");
  const [showShippingModal, setShowShippingModal] = useState(false);
  const [isValidatingPromo, setIsValidatingPromo] = useState(false);
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [checkingAvailability, setCheckingAvailability] = useState(false);
  const [totalNavHeight, setTotalNavHeight] = useState(112); // Default navbar height
  const [cartItemsShippingOptions, setCartItemsShippingOptions] = useState<
    Record<string, string[]>
  >({});
  const [loadingItemShipping, setLoadingItemShipping] = useState(true);
  const [editingQuantity, setEditingQuantity] = useState<string | null>(null);
  const [tempQuantity, setTempQuantity] = useState<string>("");
  // Initialize shipping loading state based on initial cart and address state
  const [isDebouncingShipping, setIsDebouncingShipping] = useState(() => {
    // Show loading by default if there are cart items and address is complete
    // This ensures loading state is shown during initial debounce period when navigating to cart
    return cartItems.length > 0 && isAddressComplete;
  });
  
  // Rush'd pricing state
  const [rushdPricingQuote, setRushdPricingQuote] = useState<RushdPricingQuote | null>(null);
  const [loadingRushdPricing, setLoadingRushdPricing] = useState(false);
  const [calculatedMemberRate, setCalculatedMemberRate] = useState<number>(15); // Dynamic fallback member rate
  
  // Final express rate state (after express discount is applied to DHL or fallback)
  const [finalExpressRate, setFinalExpressRate] = useState<number | null>(null);
  const [loadingFinalExpressRate, setLoadingFinalExpressRate] = useState(true);
  const [rushdTreats, setRushdTreats] = useState<RushdTreat[]>([]);
  const [rushdTreatsLoading, setRushdTreatsLoading] = useState(false);
  const isJamaicaAddress = (shippingAddress?.value?.address?.country || "").toUpperCase() === "JM";

  // Debounce cart items changes for shipping rate fetching
  const [debouncedCartItems] = useDebounce(cartItems, 3000);

  // Listen for navigation height updates from NavBar
  useEffect(() => {
    const handleNavHeightChange = (event: CustomEvent) => {
      setTotalNavHeight(event.detail.height);
    };

    // Listen for height updates from NavBar
    window.addEventListener(
      "navHeightUpdate",
      handleNavHeightChange as EventListener
    );

    return () => {
      window.removeEventListener(
        "navHeightUpdate",
        handleNavHeightChange as EventListener
      );
    };
  }, []);

  // Auto-load default address when cart page loads (not when user is actively changing addresses)
  useEffect(() => {
    if (user && userProfile && !loadingProfile) {
      const savedAddresses = userProfile.addresses || [];
      if (savedAddresses.length > 0) {
        // Always prioritize default address if it exists
        const defaultAddress = savedAddresses.find(addr => addr.isDefault);
        
        if (defaultAddress) {
          // Convert saved address to shipping address format
          const convertedAddress = {
            complete: true,
            value: {
              name: defaultAddress.fullName,
              phone: defaultAddress.phoneNumber || "",
              address: {
                line1: defaultAddress.line1,
                line2: defaultAddress.line2 || "",
                city: defaultAddress.city,
                state: defaultAddress.state,
                postal_code: defaultAddress.zipCode,
                country: defaultAddress.country,
              }
            }
          };
          
          setShippingAddress(convertedAddress);
        } else if (!isAddressComplete) {
          // Only load first address as fallback if no address is currently set
          const firstAddress = savedAddresses[0];
          if (firstAddress) {
            const convertedAddress = {
              complete: true,
              value: {
                name: firstAddress.fullName,
                phone: firstAddress.phoneNumber || "",
                address: {
                  line1: firstAddress.line1,
                  line2: firstAddress.line2 || "",
                  city: firstAddress.city,
                  state: firstAddress.state,
                  postal_code: firstAddress.zipCode,
                  country: firstAddress.country,
                }
              }
            };
            
            setShippingAddress(convertedAddress);
          }
        }
      }
    }
  }, [user, userProfile, loadingProfile]); // Removed setShippingAddress and isAddressComplete from deps

  // Track when cart items or address changes to show loading state during debounce period
  useEffect(() => {
    if (cartItems.length > 0 && isAddressComplete) {
      setIsDebouncingShipping(true);
    } else if (cartItems.length === 0) {
      // Clear loading state if cart becomes empty
      setIsDebouncingShipping(false);
    }
  }, [cartItems, isAddressComplete]);

  // Calculate cart totals first (needed by useEffects below)
  const usdSubtotal = getCartTotalWithDiscounts(); // Use new method that considers line-item discounts
  const usdBaseShippingCost = getShippingCostSync();
  const usdShipping = getFinalShippingCost();
  const usdPriorityFee = (isMember && isActive) ? 0 : getPriorityProcessingFee();
  const usdDiscount = getDiscountAmount();
  const usdTotal = getFinalTotal();

  // Fetch DHL Express rate when debounced cart items or address changes
  useEffect(() => {
    if (isAddressComplete && debouncedCartItems.length > 0 && shippingAddress?.value?.address) {
      
      fetchDhlExpressRate(debouncedCartItems, shippingAddress).finally(() => {
        setIsDebouncingShipping(false);
      });
    } else if (debouncedCartItems.length === 0) {
      // Clear loading state if cart becomes empty
      setIsDebouncingShipping(false);
    }
  }, [
    isAddressComplete, 
    debouncedCartItems, // Use debounced cart items to prevent rapid API calls
    shippingAddress?.value?.address?.postal_code, 
    shippingAddress?.value?.address?.country,
    fetchDhlExpressRate
  ]);

  // Fetch Rush'd pricing quote when cart or address changes
  useEffect(() => {
    const fetchRushdPricing = async () => {
      if (!isAddressComplete || debouncedCartItems.length === 0 || !shippingAddress?.value?.address) {
        setRushdPricingQuote(null);
        return;
      }

      setLoadingRushdPricing(true);
      
      try {
        const quote = await getExpressPricingQuote(
          debouncedCartItems,
          shippingAddress,
          usdSubtotal
        );
        setRushdPricingQuote(quote);
      } catch (error) {
        console.error('Error fetching Rush\'d pricing:', error);
        setRushdPricingQuote(null);
      } finally {
        setLoadingRushdPricing(false);
      }
    };

    fetchRushdPricing();
  }, [
    isAddressComplete,
    debouncedCartItems,
    shippingAddress?.value?.address?.postal_code,
    shippingAddress?.value?.address?.country,
    usdSubtotal,
    getExpressPricingQuote
  ]);

  // Calculate final express rate (after express discount applied to DHL or fallback)
  useEffect(() => {
    const calculateFinalExpressRate = async () => {
      try {
        setLoadingFinalExpressRate(true);
        
        // For Jamaican addresses with configured JM price, bypass rate fetching and use jmPrice
        const expressTier = availableShippingTiers.find(tier => tier.id === 'express');
        if (isJamaicaAddress && expressTier?.jmPrice != null) {
          setFinalExpressRate(expressTier.jmPrice);
          setLoadingFinalExpressRate(false);
          return;
        }
        
        // If DHL rate is available and loaded successfully, use it (already has express discount applied)
        if (dhlExpressRate !== null && !dhlRateError && !loadingDhlRate) {
          setFinalExpressRate(dhlExpressRate);
          setLoadingFinalExpressRate(false);
          return;
        }
        
        // If address is complete and DHL is still loading or debouncing, stay in loading state
        if (isAddressComplete && (loadingDhlRate || isDebouncingShipping)) {
          // Don't set loading to false yet - wait for DHL rate fetch to complete
          return;
        }
        
        // If DHL rate failed or unavailable, apply express discount to fallback tier cost
        if (expressTier) {
          // Get express discount percentage and apply it to fallback cost
          const discountPercent = await getExpressShippingDiscountPercent();
          const discountedFallbackRate = applyShippingDiscount(expressTier.cost, discountPercent);
          setFinalExpressRate(discountedFallbackRate);
        } else {
          // No express tier available, set to null
          setFinalExpressRate(null);
        }
        
        setLoadingFinalExpressRate(false);
      } catch (error) {
        console.error('Error calculating final express rate:', error);
        setFinalExpressRate(null);
        setLoadingFinalExpressRate(false);
      }
    };

    calculateFinalExpressRate();
  }, [dhlExpressRate, dhlRateError, loadingDhlRate, availableShippingTiers, isAddressComplete, isDebouncingShipping]);

  // Auto-enable priority processing for Rush'd members
  useEffect(() => {
    if (isMember && isActive) {
      setPriorityProcessing(true);
    }
  }, [isMember, isActive, setPriorityProcessing]);

  // Sync Rush'd treats for active members
  useEffect(() => {
    let cancelled = false;

    const syncTreats = async () => {
      if (!isMember || !isActive) {
        setRushdTreats([]);
        syncRushdTreatItems([], false);
        return;
      }

      setRushdTreatsLoading(true);
      try {
        const treats = await getActiveRushdTreats();
        if (cancelled) return;
        setRushdTreats(treats);
        await syncRushdTreatItems(
          treats.map((treat) => ({
            productId: treat.productId,
            productName: treat.productName,
            productImage: treat.productImage,
            quantity: treat.quantity || 1,
          })),
          true
        );
      } catch (error) {
        console.error("Error syncing Rush'd treats:", error);
      } finally {
        if (!cancelled) {
          setRushdTreatsLoading(false);
        }
      }
    };

    syncTreats();

    return () => {
      cancelled = true;
    };
  }, [isMember, isActive, syncRushdTreatItems]);

  // Calculate dynamic Rush'd member rate fallback
  useEffect(() => {
    const calculateMemberRateFallback = async () => {
      try {
        // Prioritize local calculation over API quote for better accuracy
        // The API quote may not account for the latest express discount applied locally
        // So we'll use our local calculation and only fall back to API if local calculation fails

        // Wait for final express rate calculation to complete
        if (loadingFinalExpressRate) {
          return;
        }

        // If no final express rate available, use ultimate fallback
        if (finalExpressRate === null) {
          setCalculatedMemberRate(15);
          return;
        }

        // Wait for Rush'd settings to load
        if (rushdSettingsLoading) {
          return;
        }

        // Get Rush'd configuration from real-time settings
        const rushdConfig = getRushdConfigFromSettings(rushdSettings);
        if (!rushdConfig.programActive) {
          setCalculatedMemberRate(15);
          return;
        }

        // Use the final express rate (already discounted) as the base for Rush'd calculations
        // This ensures Rush'd discount applies to the express-discounted rate, not the original DHL rate
        const expressDiscountedRate = finalExpressRate;

        // Calculate Rush'd member rate from express-discounted rate
        const memberRate = calculateRushdMemberRate(expressDiscountedRate, rushdConfig);
        
        
        setCalculatedMemberRate(memberRate);
      } catch (error) {
        console.error('Error calculating dynamic Rush\'d member rate:', error);
        setCalculatedMemberRate(15); // Ultimate fallback
      }
    };

    calculateMemberRateFallback();
  }, [
    rushdPricingQuote?.memberPrice,
    finalExpressRate,
    loadingFinalExpressRate,
    rushdSettings,
    rushdSettingsLoading
  ]);

  // Load shipping options for each cart item
  useEffect(() => {
    const loadCartItemShippingOptions = async () => {
      if (cartItems.length === 0) {
        setCartItemsShippingOptions({});
        setLoadingItemShipping(false);
        return;
      }

      try {
        setLoadingItemShipping(true);
        const itemShippingMap: Record<string, string[]> = {};

        // Fetch product details for each cart item to get their available shipping options
        const productPromises = cartItems.map(async (item) => {
          try {
            const product = await fetchProductById(item.id);
            if (product) {
              // If product has no specific shipping options, it can use all active shipping tiers
              itemShippingMap[item.id] =
                product.availableShippingOptions &&
                product.availableShippingOptions.length > 0
                  ? product.availableShippingOptions
                  : getActiveShippingTiers().map((tier) => tier.id);
            } else {
              // Fallback to all active tiers if product not found
              itemShippingMap[item.id] = getActiveShippingTiers().map(
                (tier) => tier.id
              );
            }
          } catch (error) {
            console.error(
              `Error loading shipping options for product ${item.id}:`,
              error
            );
            // Fallback to all active tiers on error
            itemShippingMap[item.id] = getActiveShippingTiers().map(
              (tier) => tier.id
            );
          }
        });

        await Promise.all(productPromises);
        setCartItemsShippingOptions(itemShippingMap);
      } catch (error) {
        console.error("Error loading cart item shipping options:", error);
        // Fallback: assume all items can use all shipping options
        const fallbackMap: Record<string, string[]> = {};
        cartItems.forEach((item) => {
          fallbackMap[item.id] = getActiveShippingTiers().map(
            (tier) => tier.id
          );
        });
        setCartItemsShippingOptions(fallbackMap);
      } finally {
        setLoadingItemShipping(false);
      }
    };

    loadCartItemShippingOptions();
  }, [cartItems, getActiveShippingTiers]);

  // Calculate common shipping options available to all cart items for the current country
  const getCommonShippingOptions = () => {
    if (cartItems.length === 0 || loadingItemShipping) return [];

    // Get shipping country from address directly
    const shippingCountry = shippingAddress?.value?.address?.country;
    
    // Get tiers available in the current country (for backward compatibility)
    const availableTiers = getAvailableShippingTiers(shippingCountry);
    if (Object.keys(cartItemsShippingOptions).length === 0) return availableTiers;

    // Find shipping options that are available for ALL cart items AND the current country
    const itemShippingLists = cartItems.map(
      (item) => cartItemsShippingOptions[item.id] || []
    );

    if (itemShippingLists.length === 0) return availableTiers;

    // Get intersection of territory-available tiers and item-compatible options
    const commonOptions = availableTiers.filter((tier) =>
      itemShippingLists.every((itemOptions) => itemOptions.includes(tier.id))
    );

    return commonOptions.sort((a, b) => a.sortOrder - b.sortOrder);
  };

  const commonShippingOptions = getCommonShippingOptions();

  // Memoize cartProductIds to prevent unnecessary re-renders
  const cartProductIds = useMemo(() => {
    return cartItems.map(item => item.id);
  }, [cartItems]);

  // Helper function to check if standard shipping should be disabled
  const shouldStandardShippingBeDisabled = () => {
    // Don't disable while still loading shipping options
    if (loadingItemShipping || Object.keys(cartItemsShippingOptions).length === 0) return false;
    
    // Check if any cart item doesn't support standard shipping
    return cartItems.some(item => {
      const itemOptions = cartItemsShippingOptions[item.id] || [];
      return !itemOptions.includes('standard');
    });
  };

  // Helper function to check if a tier is disabled due to territory restrictions
  const isTierDisabledDueToTerritory = (tier: ShippingTier, country?: string) => {
    if (!country) return false; // No country specified, not disabled
    return !isTierAvailableInCountry(tier, country);
  };


  // Auto-select appropriate shipping option with preference for available tiers
  useEffect(() => {
    if (!loadingItemShipping) {
      // Get all active tiers and current shipping country
      const shippingCountry = shippingAddress?.value?.address?.country;
      const allTiers = getAllActiveShippingTiers();
      
      // Check if current selection is valid and not disabled
      const currentTier = allTiers.find(tier => tier.id === shippingOption);
      
      // Check various disabled states for current selection
      const isCurrentSelectionDisabledDueToItems = (shippingOption === 'standard' && shouldStandardShippingBeDisabled());
      const isCurrentSelectionDisabledDueToTerritory = currentTier ? isTierDisabledDueToTerritory(currentTier, shippingCountry) : false;
      const isCurrentSelectionDisabled = isCurrentSelectionDisabledDueToItems || isCurrentSelectionDisabledDueToTerritory;
      
      if (!currentTier || isCurrentSelectionDisabled) {
        // Need to select a new option - find the first available (non-disabled) tier
        let selectedNewOption = false;
        
        // Priority 1: Try express if available and not disabled
        const expressTier = allTiers.find(tier => tier.id === 'express');
        if (expressTier && !isTierDisabledDueToTerritory(expressTier, shippingCountry)) {
          // Check if express is compatible with items
          const hasExpressCompatibleItem = cartItems.some(item => {
            const itemOptions = cartItemsShippingOptions[item.id] || [];
            return itemOptions.includes('express');
          });
          
          if (hasExpressCompatibleItem || Object.keys(cartItemsShippingOptions).length === 0) {
            setShippingOption('express');
            selectedNewOption = true;
          }
        }
        
        // Priority 2: Try standard if not disabled and not already tried
        if (!selectedNewOption) {
          const standardTier = allTiers.find(tier => tier.id === 'standard');
          if (standardTier && 
              !shouldStandardShippingBeDisabled() && 
              !isTierDisabledDueToTerritory(standardTier, shippingCountry)) {
            setShippingOption('standard');
            selectedNewOption = true;
          }
        }
        
        // Priority 3: Use first common option (available for items and territory)
        if (!selectedNewOption && commonShippingOptions.length > 0) {
          setShippingOption(commonShippingOptions[0].id as ShippingOption);
          selectedNewOption = true;
        }
        
        // Fallback: Use first tier that's not disabled due to territory
        if (!selectedNewOption) {
          const firstAvailableTier = allTiers.find(tier => 
            !isTierDisabledDueToTerritory(tier, shippingCountry)
          );
          if (firstAvailableTier) {
            setShippingOption(firstAvailableTier.id as ShippingOption);
          }
        }
      }
    }
  }, [
    commonShippingOptions,
    shippingOption,
    setShippingOption,
    loadingItemShipping,
    shippingAddress?.value?.address?.country,
    getAllActiveShippingTiers,
    isTierAvailableInCountry,
    cartItems,
    cartItemsShippingOptions,
  ]);

  // Keep current shipping selection even when address is removed - all options will show "Address Required"
  // This provides better UX as user doesn't lose their selection preference

  // Helper function to check if an item has limited shipping options
  const hasLimitedShippingOptions = (itemId: string) => {
    // Don't show badge while still loading shipping options
    if (loadingItemShipping) return false;
    
    const itemOptions = cartItemsShippingOptions[itemId] || [];
    const allOptions = getActiveShippingTiers().map((tier) => tier.id);
    return itemOptions.length < allOptions.length;
  };

  // Helper function to get items that only support express shipping
  const getExpressOnlyItems = () => {
    if (loadingItemShipping || Object.keys(cartItemsShippingOptions).length === 0) return [];
    
    return cartItems.filter(item => {
      const itemOptions = cartItemsShippingOptions[item.id] || [];
      return itemOptions.includes('express') && !itemOptions.includes('standard');
    });
  };

  // Helper function to get items that require express shipping (have limited options)
  const getItemsRequiringExpress = () => {
    if (loadingItemShipping || Object.keys(cartItemsShippingOptions).length === 0) return [];
    
    return cartItems.filter(item => {
      const itemOptions = cartItemsShippingOptions[item.id] || [];
      const allOptions = getActiveShippingTiers().map((tier) => tier.id);
      return itemOptions.length < allOptions.length && itemOptions.includes('express');
    });
  };

  // Helper function to get tiers that are disabled due to territory restrictions
  const getTerritoryDisabledTiers = (country?: string) => {
    if (!country) return [];
    
    return getAllActiveShippingTiers().filter(tier => 
      !isTierAvailableInCountry(tier, country)
    );
  };

  const handleQuantityChange = (id: string, change: number) => {
    const item = cartItems.find((item) => item.id === id);
    if (!item) return;

    // Prevent quantity changes for free items (price $0)
    if (item.price === 0) {
      return;
    }

    const newQuantity = item.quantity + change;
    if (newQuantity >= 1) {
      updateQuantity(id, newQuantity);
    }
  };

  const handleRemoveItem = (id: string) => {
    removeFromCart(id);
    toast({
      title: "Item Removed",
      description: "The item has been removed from your cart.",
      variant: "default",
    });
  };

  const handleRemoveRushdTreat = (id: string, name: string) => {
    removeFromCart(id);
    toast({
      title: "Rush'd Treat Removed",
      description: `${name} was removed from your cart.`,
      variant: "default",
    });
  };

  const handleQuantityClick = (itemId: string, currentQuantity: number) => {
    const item = cartItems.find(item => item.id === itemId);
    // Don't allow editing for free items
    if (item && item.price === 0) return;
    
    setEditingQuantity(itemId);
    setTempQuantity(currentQuantity.toString());
  };

  const handleQuantityInputChange = (value: string) => {
    // Only allow positive integers
    if (value === "" || /^\d+$/.test(value)) {
      setTempQuantity(value);
    }
  };

  const handleQuantitySubmit = (itemId: string) => {
    const newQuantity = parseInt(tempQuantity) || 1;
    if (newQuantity >= 1) {
      updateQuantity(itemId, newQuantity);
    }
    setEditingQuantity(null);
    setTempQuantity("");
  };

  const handleQuantityCancel = () => {
    setEditingQuantity(null);
    setTempQuantity("");
  };

  // Helper function to get appropriate toast title based on error message
  const getDiscountErrorTitle = (errorMessage: string): string => {
    if (errorMessage.includes('Please enter a discount code')) {
      return 'Missing Code';
    }
    if (errorMessage.includes('Cart subtotal must be at least')) {
      return 'Minimum Order Required';
    }
    if (errorMessage.includes('Discount code has expired')) {
      return 'Code Expired';
    }
    if (errorMessage.includes('usage limit reached')) {
      return 'Code No Longer Available';
    }
    if (errorMessage.includes('You have already used this discount code')) {
      return 'Code Already Used';
    }
    if (errorMessage.includes('Invalid discount code')) {
      return 'Invalid Code';
    }
    if (errorMessage.includes("JamPack'd Rush'd member")) {
      return "Rush'd Members Only";
    }
    if (errorMessage.includes('Failed to validate discount code')) {
      return 'Validation Error';
    }
    return 'Invalid Discount Code'; // Default fallback
  };

  const handleApplyPromo = async () => {
    if (!promoCode.trim()) {
      toast({
        title: "Missing Code",
        description: "Please enter a discount code",
        variant: "destructive",
        duration: 6000, // Show for 6 seconds
      });
      return;
    }

    setIsValidatingPromo(true);
    // Set flag to prevent discount invalidation during application
    setApplyingDiscount(true);

    try {
      // Validate discount code based on original cart subtotal (excluding shipping and fees)
      // Use original subtotal for validation, not discounted subtotal
      const originalUsdSubtotal = getCartTotal();
      
      // Use the appropriate validation function based on authentication status
      // For authenticated users: check user-specific usage limits
      // For unauthenticated users: allow discount preview (final validation happens at checkout)
      const validation = user 
        ? await validateDiscountCode(
            promoCode.trim(),
            user.uid,
            originalUsdSubtotal, // Only use original subtotal for discount validation
            user.email || "",
            {
              getConvertedItemPrice,
              formatDisplayAmount,
              selectedCurrency
            }
          )
        : await validateDiscountCodeForCart(
            promoCode.trim(),
            originalUsdSubtotal, // Only use original subtotal for discount validation
            {
              getConvertedItemPrice,
              formatDisplayAmount,
              selectedCurrency
            }
          );

      if (
        validation.isValid &&
        validation.discount &&
        validation.discountAmount !== undefined
      ) {
        // Create the initial discount object
        const discountToApply: AppliedDiscount = {
          discount: validation.discount,
          discountAmount: validation.discountAmount,
        };

        // For percentage and fixed discounts, calculate line-item discounts
        if (validation.discount.type === 'percentage' || validation.discount.type === 'fixed') {
          // Get line-item discounts using the new proportional system
          const itemsWithDiscounts = applyDiscountToCartItems(discountToApply);
          
          // Extract line-item discount data from the applied items
          const lineItemDiscounts: { [itemId: string]: { discountAmount: number; discountPercentage: number; } } = {};
          
          itemsWithDiscounts.forEach(item => {
            if (item.discountAmount !== undefined && item.discountPercentage !== undefined) {
              lineItemDiscounts[item.id] = {
                discountAmount: item.discountAmount,
                discountPercentage: item.discountPercentage
              };
            }
          });

          // Add line-item discounts to the applied discount
          discountToApply.lineItemDiscounts = lineItemDiscounts;
        }

        setAppliedDiscount(discountToApply);
        setPromoCode("");
        const usdSavingsAmount =
          validation.discount.type === "free_shipping"
            ? usdBaseShippingCost
            : validation.discountAmount;
        
        // Convert USD savings amount to selected currency
        const convertedSavingsAmount = getConvertedItemPrice(usdSavingsAmount);

        toast({
          title: "Discount Applied!",
          description: `${
            validation.discount.code
          } - You saved ${formatDisplayAmount(convertedSavingsAmount)} ${selectedCurrency}`,
          duration: 5000, // Show for 5 seconds for success
        });
      } else {
        const errorMessage = validation.error || "This discount code is not valid";
        toast({
          title: getDiscountErrorTitle(errorMessage),
          description: errorMessage,
          variant: "destructive",
          duration: 7000, // Show for 7 seconds for discount errors
        });
      }
    } catch (error) {
      console.error("Error applying discount:", error);
      toast({
        title: "Validation Error",
        description: "Failed to apply discount code. Please try again.",
        variant: "destructive",
        duration: 6000, // Show for 6 seconds
      });
    } finally {
      setIsValidatingPromo(false);
      // Clear the flag to allow normal discount invalidation
      setApplyingDiscount(false);
    }
  };

  const handleRemovePromo = () => {
    setAppliedDiscount(null);
    toast({
      title: "Discount Removed",
      description: "The discount code has been removed from your order",
      duration: 4000, // Show for 4 seconds
    });
  };

  const handleClearCart = () => {
    clearCart();
    toast({
      title: "Cart Cleared",
      description: "All items have been removed from your cart.",
      variant: "default",
      duration: 4000, // Show for 4 seconds
    });
  };

  const handleShippingAddressConfirmed = (address: any) => {
    setShippingAddress(address);
    toast({
      title: "Shipping Address Set",
      description: "Your shipping address has been saved for checkout.",
      duration: 4000, // Show for 4 seconds
    });
  };

  const handleCheckout = async () => {
    // Check if any cart items are disabled before proceeding
    setCheckingAvailability(true);

    try {
      const productIds = cartItems.map((item) => item.id);
      const availabilityCheck = await checkProductsAvailability(productIds);

      // If there are disabled products, notify user and remove them
      if (availabilityCheck.disabled.length > 0) {
        // Show toast notifications for each disabled product
        availabilityCheck.disabled.forEach((disabledProduct) => {
          toast({
            title: "Product No Longer Available",
            description: `"${disabledProduct.name}" is no longer available and has been removed from your cart.`,
            variant: "destructive",
          });

          // Remove the disabled product from cart
          removeFromCart(disabledProduct.id);
        });

        // Show summary toast if multiple products were removed
        if (availabilityCheck.disabled.length > 1) {
          toast({
            title: "Cart Updated",
            description: `${availabilityCheck.disabled.length} products that are no longer available have been removed from your cart.`,
            variant: "destructive",
          });
        }

        // If all products were disabled, don't proceed to checkout
        if (availabilityCheck.available.length === 0) {
          setCheckingAvailability(false);
          return;
        }
      }

      // Enforce minimum order total after shipping (currency-aware)
      const minimumUsd = 35;
      const minimumConverted = getConvertedItemPrice(minimumUsd);
      if (convertedTotal < minimumConverted) {
        setCheckingAvailability(false);
        const thresholdText =
          selectedCurrency === "USD"
            ? `$${minimumUsd.toFixed(2)} USD`
            : `${formatDisplayAmount(minimumConverted)} (equiv. $${minimumUsd.toFixed(2)} USD)`;
        toast({
          title: "Order total too low",
          description: `Your order total after shipping must be at least ${thresholdText} to proceed to checkout.`,
          variant: "destructive",
        });
        return;
      }

      // If user is authenticated and there are still available products, proceed to checkout
      if (user) {
        // Persist a snapshot of shipping data for checkout to avoid recomputation drift
        try {
          const cartItemsHash = JSON.stringify(cartItems.map(item => ({ id: item.id, quantity: item.quantity })));
          const shippingSnapshot = {
            timestamp: Date.now(),
            cartItemsHash,
            shippingOption,
            rushdMember: !!(isMember && isActive),
            rushdMemberExpressUSD: (shippingOption === 'express' && isMember && isActive && calculatedMemberRate) ? calculatedMemberRate : null,
          };
          sessionStorage.setItem('checkout-shipping-snapshot', JSON.stringify(shippingSnapshot));
        } catch (_) {
          // Non-fatal; continue to checkout
        }
        // Set checkout flow flag before navigation
        sessionStorage.setItem(
          "checkout-flow-initiated",
          JSON.stringify({
            timestamp: Date.now(),
            userId: user.uid,
            cartItems: availabilityCheck.available.length,
          })
        );

        // User is authenticated, proceed to checkout
        navigate("/checkout");
      } else {
        // User is not authenticated, show auth modal
        setAuthModalOpen(true);
        toast({
          title: "Authentication Required",
          description: "Please log in or sign up to proceed to checkout",
          variant: "default",
        });
      }
    } catch (error) {
      console.error("Error checking product availability:", error);
      toast({
        title: "Error",
        description: "Unable to verify product availability. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCheckingAvailability(false);
    }
  };

  // USD calculations moved above useEffects to prevent hoisting issues

  // Currency-aware calculations for display
  const convertedSubtotal = (() => {
    if (appliedDiscount && (appliedDiscount.discount.type === 'percentage' || appliedDiscount.discount.type === 'fixed')) {
      // Sum up the actual discounted totals displayed in line items for mathematical consistency
      // This matches exactly what the user sees in the line item display
      const itemsWithDiscounts = applyDiscountToCartItems(appliedDiscount);
      
      return cartItems.reduce((total, item) => {
        // Get the unit price (sale price if on sale, otherwise regular price)
        const unitPriceUsd = item.onSale && item.onSalePrice && item.onSalePrice < item.price
          ? item.onSalePrice
          : item.price;
        
        // Convert unit price to selected currency once
        const convertedUnitPrice = getConvertedItemPrice(unitPriceUsd);
        
        // Calculate original total: converted unit price × quantity
        const originalTotal = convertedUnitPrice * item.quantity;
        
        // Check if this item has a discount applied
        const discountedItem = itemsWithDiscounts.find(discountItem => discountItem.id === item.id);
        const hasDiscount = discountedItem && discountedItem.discountAmount && discountedItem.discountAmount > 0;
        
        if (hasDiscount && discountedItem.discountedTotal !== undefined) {
          // Calculate discount amount using the same proportional distribution logic as line item display
          const convertedDiscountAmount = (() => {
            if (!appliedDiscount || selectedCurrency === 'USD') {
              // For USD or no discount, use direct conversion
              return getConvertedItemPrice(discountedItem.discountAmount!);
            }
            
            // Get all discounted items for proportional calculation
            const allDiscountedItems = itemsWithDiscounts.filter(item => item.discountAmount && item.discountAmount > 0);
            
            // Calculate total USD discount across all items
            const totalUsdDiscount = allDiscountedItems.reduce((sum, item) => sum + (item.discountAmount || 0), 0);
            
            // Convert total discount to display currency
            const totalConvertedDiscount = getConvertedItemPrice(totalUsdDiscount);
            
            // Calculate this item's proportion of total USD discount
            const itemDiscountProportion = (discountedItem.discountAmount!) / totalUsdDiscount;
            
            // Find this item's index for "last item gets remainder" logic
            const currentItemIndex = allDiscountedItems.findIndex(item => item.id === discountedItem.id);
            const isLastItem = currentItemIndex === allDiscountedItems.length - 1;
            
            if (isLastItem) {
              // Last item: get exact remainder to ensure total precision
              const otherItemsTotal = allDiscountedItems.slice(0, -1).reduce((sum, otherItem) => {
                const otherProportion = (otherItem.discountAmount || 0) / totalUsdDiscount;
                return sum + Math.round(totalConvertedDiscount * otherProportion * 100) / 100;
              }, 0);
              return totalConvertedDiscount - otherItemsTotal;
            } else {
              // All other items: calculate proportional discount and round
              return Math.round(totalConvertedDiscount * itemDiscountProportion * 100) / 100;
            }
          })();
          
          // Calculate discounted total mathematically: original - discount
          const convertedDiscountedTotal = Math.max(0, originalTotal - convertedDiscountAmount);
          
          return total + convertedDiscountedTotal;
        } else {
          // No discount, add original total
          return total + originalTotal;
        }
      }, 0);
    } else {
      // Use original calculation for free shipping discounts or no discount
      return cartItems.reduce((total, item) => {
        const unitPriceUsd = item.onSale && item.onSalePrice && item.onSalePrice < item.price 
          ? item.onSalePrice 
          : item.price;
        const convertedUnitPrice = getConvertedItemPrice(unitPriceUsd);
        return total + (convertedUnitPrice * item.quantity);
      }, 0);
    }
  })();

  const convertedBaseShippingCost = getConvertedItemPrice(usdBaseShippingCost);
  
  // Calculate shipping cost with proper priority hierarchy (same as checkout)
  const getEffectiveShippingCost = () => {
    // First priority: Free shipping discount codes
    if (appliedDiscount?.discount.type === "free_shipping") {
      return 0;
    }
    
    const tier = getShippingTierById(shippingOption);
    
    // Second priority: Check free shipping threshold (currency-aware)
    if (tier?.freeShippingThreshold && tier.freeShippingThreshold > 0) {
      const threshold =
        selectedCurrency === "USD"
          ? tier.freeShippingThreshold
          : getConvertedItemPrice(tier.freeShippingThreshold);
      const subtotalForThreshold =
        selectedCurrency === "USD" ? usdSubtotal : convertedSubtotal;
      if (subtotalForThreshold >= threshold) {
        return 0; // Free shipping threshold met
      }
    }
    
    // Third priority: Rush'd member pricing for Express shipping if eligible
    if (
      shippingOption === "express" &&
      rushdPricingQuote &&
      rushdPricingQuote.isEligible &&
      isMember &&
      isActive &&
      rushdPricingQuote.memberPrice !== undefined
    ) {
      return getConvertedItemPrice(calculatedMemberRate);
    }
    
    // Fallback: Use regular shipping cost, with JM override if configured
    const baseCostUsd =
      isJamaicaAddress && tier?.jmPrice != null ? tier.jmPrice : usdShipping;
    return getConvertedItemPrice(baseCostUsd);
  };
  
  const convertedShipping = getEffectiveShippingCost();
  const convertedPriorityFee = getConvertedItemPrice(usdPriorityFee);
  // For percentage/fixed discounts, discount is already applied at line-item level
  const convertedDiscount = appliedDiscount?.discount.type === 'free_shipping' ? 0 : 0;
  const convertedTotal = convertedSubtotal + convertedShipping + convertedPriorityFee - convertedDiscount;

  const displayCartItems = useMemo(() => {
    if (!isMember || !isActive) {
      return cartItems;
    }

    const rushdTreatItems = cartItems.filter((item) => item.rushdTreat);

    if (rushdTreatItems.length === 0) {
      return cartItems;
    }

    const regularItems = cartItems.filter((item) => !item.rushdTreat);

    // Only reorder when necessary to keep minimal impact on other carts
    return [...rushdTreatItems, ...regularItems];
  }, [cartItems, isMember, isActive]);

  // Generate SEO data for Cart page
  const seoData = validateSEOData(getCartSEO());

  return (
    <>
      <Helmet>
        {/* Basic Meta Tags */}
        <title>{seoData.title}</title>
        <meta name="description" content={seoData.description} />
        <meta name="author" content={seoData.author} />
        
        {/* Open Graph Tags for Social Sharing */}
        <meta property="og:title" content={seoData.title} />
        <meta property="og:description" content={seoData.description} />
        <meta property="og:image" content={seoData.image} />
        <meta property="og:url" content={seoData.url} />
        <meta property="og:type" content={seoData.type} />
        <meta property="og:site_name" content={seoData.siteName} />
        <meta property="og:locale" content={seoData.locale} />
        
        {/* Twitter Card Tags */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seoData.title} />
        <meta name="twitter:description" content={seoData.description} />
        <meta name="twitter:image" content={seoData.image} />
        
        {/* Additional SEO Tags */}
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href={seoData.url} />
      </Helmet>
      
      <div
        className="flex flex-col"
        style={{ minHeight: `calc(100vh - ${totalNavHeight}px)` }}
      >
      {/* Auth Modal */}
      <AuthModalWrapper
        isOpen={authModalOpen}
        onOpenChange={setAuthModalOpen}
        defaultTab="login"
      />

      <main className="flex-grow relative bg-gradient-to-br from-gray-50 via-white to-gray-100 overflow-hidden">
        {/* Animated Background Elements */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          {/* Floating Geometric Shapes */}
          <div className="absolute top-20 left-20 w-16 h-16 bg-gradient-to-br from-jamaica-green/10 to-jamaica-green/5 rounded-2xl transform rotate-45 animate-pulse delay-300"></div>
          <div className="absolute top-40 right-32 w-12 h-12 bg-jamaica-yellow/15 rounded-full animate-bounce delay-500"></div>
          <div className="absolute bottom-32 left-1/4 w-20 h-20 bg-gradient-to-br from-jamaica-yellow/8 to-jamaica-green/8 rounded-3xl transform rotate-12 animate-ping delay-700"></div>
          <div className="absolute bottom-20 right-20 w-14 h-14 bg-jamaica-green/12 rounded-lg transform -rotate-12 animate-pulse delay-900"></div>
          
          {/* Diagonal Lines Pattern */}
          <div className="absolute inset-0 opacity-5">
            <div className="absolute inset-0" style={{
              backgroundImage: `
                linear-gradient(45deg, rgba(0, 116, 47, 0.1) 1px, transparent 1px),
                linear-gradient(-45deg, rgba(255, 201, 1, 0.1) 1px, transparent 1px)
              `,
              backgroundSize: '80px 80px, 120px 120px',
              backgroundPosition: '0 0, 40px 40px'
            }}></div>
          </div>
          
          {/* Gradient Blobs */}
          <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-l from-jamaica-green/3 to-jamaica-yellow/3 rounded-full mix-blend-multiply filter blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-gradient-to-r from-jamaica-yellow/4 to-jamaica-green/3 rounded-full mix-blend-multiply filter blur-2xl animate-pulse delay-1500"></div>
        </div>
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Enhanced Hero Section */}
          <div className="mb-12 text-center">
            <div className="inline-flex items-center gap-3 mb-6 sm:mb-4">
              <div className="relative">
                <div className="w-10 h-10 sm:w-14 sm:h-14 bg-gradient-to-br from-jamaica-green to-jamaica-green/80 rounded-2xl flex items-center justify-center shadow-xl transform rotate-6 sm:hover:-rotate-6 transition-transform duration-500">
                  <ShoppingCart className="w-5 h-5 sm:w-7 sm:h-7 text-white" />
                </div>
                <div className="absolute -top-2 -right-2 w-5 h-5 sm:w-6 sm:h-6 bg-jamaica-yellow rounded-full flex items-center justify-center animate-bounce">
                  <span className="text-[10px] sm:text-xs font-bold text-white">{getCartCount()}</span>
                </div>
              </div>
              <div className="bg-gradient-to-r from-jamaica-green/10 to-jamaica-yellow/10 px-3 py-1.5 sm:px-5 sm:py-2 rounded-2xl border border-jamaica-green/20 shadow-lg">
                <span className="text-xs sm:text-sm font-bold text-jamaica-black">
                  {getCartCount() === 0 ? "Shopping Cart" : `${getCartCount()} ${getCartCount() === 1 ? 'Item' : 'Items'}`}
                </span>
              </div>
            </div>

            <h1 className="text-2xl sm:text-4xl md:text-5xl font-bold text-gray-900 mb-2 sm:mb-4 leading-tight">
              <span className="bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green bg-clip-text text-transparent p-1">
                Your Shopping Cart
              </span>
            </h1>
          </div>

          {getCartCount() === 0 ? (
            <div className="text-center relative backdrop-blur-sm bg-white/80 rounded-xl sm:rounded-2xl shadow-lg sm:shadow-xl border border-white/40 hover:shadow-2xl transition-all duration-500 max-w-md sm:max-w-xl mx-auto px-4 sm:px-8 py-10 sm:py-16">
              {/* Animated Border Accent */}
              <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-60 rounded-t-2xl"></div>
              
              <div className="mb-5 sm:mb-6">
                <div className="relative inline-block">
                  <div className="bg-gradient-to-br from-jamaica-green/10 to-jamaica-yellow/10 rounded-full p-4 sm:p-6 inline-block shadow-lg">
                    <ShoppingCart className="h-12 w-12 sm:h-16 sm:w-16 text-jamaica-green" />
                  </div>
                  <div className="absolute -top-2 -right-2 w-7 h-7 sm:w-8 sm:h-8 bg-jamaica-yellow rounded-full flex items-center justify-center shadow-md">
                    <span className="text-[10px] sm:text-xs font-bold text-white">0</span>
                  </div>
                </div>
              </div>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mb-2">
                Your cart is empty
              </p>
              <p className="text-gray-600 text-sm sm:text-base mb-6 sm:mb-8 max-w-md mx-auto">
                Discover our authentic Jamaican products and add some island flavour to your cart!
              </p>
              <Button className="bg-gradient-to-r from-jamaica-green to-jamaica-green/90 text-white hover:from-jamaica-green/90 hover:to-jamaica-green shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-300 px-6 py-2.5 sm:px-8 sm:py-3 text-base sm:text-lg font-semibold rounded-lg sm:rounded-xl">
                <Link to="/products" className="flex items-center gap-2">
                  <ShoppingCart className="h-5 w-5" />
                  Start Shopping
                </Link>
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2">
                <div className="relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border border-white/40 p-6 hover:shadow-2xl transition-all duration-500 group">
                  {/* Animated Border Accent */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-t-2xl"></div>
                  <p className="text-lg font-bold text-gray-900 mb-4">
                    Cart Items
                  </p>

                  <div className="space-y-6">
                    {displayCartItems.map((item) => item.rushdTreat ? (
                      <div
                        key={item.id}
                        className="relative rounded-2xl border border-amber-200 bg-amber-50/80 p-4 sm:p-5 shadow-sm"
                      >
                        <button
                          type="button"
                          onClick={() => handleRemoveRushdTreat(item.id, item.name)}
                          className="absolute -top-2 -right-2 z-10 w-8 h-8 rounded-full bg-white border border-gray-200 shadow-md text-red-500 hover:text-red-600 hover:bg-red-50 hover:border-red-200 flex items-center justify-center transition-all duration-200 hover:shadow-lg"
                          aria-label="Remove Rush'd treat"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                        <div className="flex flex-col sm:flex-row items-start gap-4">
                          <div className="w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-white border border-amber-100">
                            <img
                              src={item.image}
                              alt={item.name}
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <div className="flex-1 space-y-3">
                            <div className="flex items-center gap-2 flex-wrap">
                              <div className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 border border-amber-200 rounded-full text-[11px] font-semibold uppercase tracking-wide">
                                <Crown className="w-3 h-3 text-amber-600" />
                                Rush'd Treat
                              </div>
                              <div className="inline-flex items-center gap-1 px-2 py-1 bg-jamaica-green/10 text-jamaica-green border border-jamaica-green/20 rounded-full text-[11px] font-semibold">
                                FREE
                              </div>
                            </div>
                            <h3 className="text-base sm:text-lg font-semibold text-gray-900 leading-tight">
                              {item.name}
                            </h3>
                            <p className="text-sm text-amber-800">
                              Surprise treat automatically added for active Rush'd members.
                            </p>
                          </div>
                          <div className="flex flex-col sm:items-end gap-1 text-sm text-gray-600">
                            <span className="font-semibold text-gray-900">Qty: {item.quantity}</span>
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div key={item.id}>
                        {/* Mobile Layout (< sm) */}
                        <div className="flex flex-col gap-4 sm:hidden relative">
                          {/* Remove button positioned at top-right */}
                          <button
                            onClick={() => handleRemoveItem(item.id)}
                            className="absolute -top-2 -right-2 z-10 w-8 h-8 rounded-full bg-white border border-gray-200 shadow-md text-red-500 hover:text-red-600 hover:bg-red-50 hover:border-red-200 flex items-center justify-center transition-all duration-200 hover:shadow-lg"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                          
                          <div className="flex gap-4">
                            <div className="w-20 h-20 rounded-md overflow-hidden flex-shrink-0">
                              <img
                                src={item.image}
                                alt={item.name}
                                className="h-full w-full object-cover"
                              />
                            </div>
                            <div className="flex-1 min-w-0 pr-6">
                              <div className="flex items-start gap-2 flex-wrap mb-2">
                                <h3 className="text-base font-medium text-gray-900 leading-tight">
                                  <Link
                                    to={`/product/${item.id}`}
                                    className="hover:text-jamaica-green transition-colors"
                                  >
                                    {item.name}
                                  </Link>
                                </h3>
                                {hasLimitedShippingOptions(item.id) && (
                                  <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 border border-green-200 rounded-md text-xs font-medium">
                                    <Info className="h-3 w-3" />
                                    Express Shipping Required
                                  </div>
                                )}
                              </div>
                              {item.price > 0 && (
                                <p className="text-sm text-gray-500">
                                  {item.onSale &&
                                  item.onSalePrice &&
                                  item.onSalePrice < item.price ? (
                                    <>
                                      <span className="text-jamaica-green font-medium">
                                        {formatDisplayAmount(getConvertedItemPrice(item.onSalePrice))}
                                      </span>
                                      <span className="line-through ml-2">
                                        {formatDisplayAmount(getConvertedItemPrice(item.price))}
                                      </span>
                                    </>
                                  ) : (
                                    <>Unit Price: {formatDisplayAmount(getConvertedItemPrice(item.price))} {selectedCurrency}</>
                                  )}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <div className="flex items-center justify-between">
                            <div className="flex items-center">
                              {item.price > 0 && (
                                <div className="flex items-center">
                                  <button
                                    onClick={() =>
                                      handleQuantityChange(item.id, -1)
                                    }
                                    className="rounded-md bg-gray-100 p-1.5 text-gray-600 hover:bg-gray-200 transition-colors"
                                  >
                                    <Minus className="h-4 w-4" />
                                  </button>
                                  {editingQuantity === item.id ? (
                                    <input
                                      type="text"
                                      value={tempQuantity}
                                      onChange={(e) => handleQuantityInputChange(e.target.value)}
                                      onBlur={() => handleQuantitySubmit(item.id)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          handleQuantitySubmit(item.id);
                                        } else if (e.key === 'Escape') {
                                          handleQuantityCancel();
                                        }
                                      }}
                                      className="px-3 py-1 font-medium w-[2rem] text-center bg-transparent border-none outline-none focus:ring-0"
                                      autoFocus
                                    />
                                  ) : (
                                    <span 
                                      className="px-3 py-1 font-medium min-w-[2rem] text-center cursor-pointer hover:bg-gray-100 rounded transition-colors"
                                      onClick={() => handleQuantityClick(item.id, item.quantity)}
                                    >
                                      {item.quantity}
                                    </span>
                                  )}
                                  <button
                                    onClick={() => handleQuantityChange(item.id, 1)}
                                    className="rounded-md bg-gray-100 p-1.5 text-gray-600 hover:bg-gray-200 transition-colors"
                                  >
                                    <Plus className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                            <div className="text-right font-medium">
                              {item.price === 0 ? (
                                <span className="text-jamaica-green font-bold">FREE</span>
                              ) : (
                                <>
                                  {(() => {
                                    // Get the unit price (sale price if on sale, otherwise regular price)
                                    const unitPriceUsd = item.onSale &&
                                      item.onSalePrice &&
                                      item.onSalePrice < item.price
                                        ? item.onSalePrice
                                        : item.price;
                                    
                                    // Convert unit price to selected currency once
                                    const convertedUnitPrice = getConvertedItemPrice(unitPriceUsd);
                                    
                                    // Calculate original total: converted unit price × quantity
                                    const originalTotal = convertedUnitPrice * item.quantity;
                                    
                                    // Check if this item has a discount applied
                                    const itemsWithDiscounts = appliedDiscount ? applyDiscountToCartItems(appliedDiscount) : cartItems;
                                    const discountedItem = itemsWithDiscounts.find(discountItem => discountItem.id === item.id);
                                    const hasDiscount = discountedItem && discountedItem.discountAmount && discountedItem.discountAmount > 0;
                                    
                                    if (hasDiscount && discountedItem.discountedTotal !== undefined) {
                                      
                                      // Calculate discount amount using proportional distribution in display currency
                                      const convertedDiscountAmount = (() => {
                                        if (!appliedDiscount || selectedCurrency === 'USD') {
                                          // For USD or no discount, use direct conversion
                                          return getConvertedItemPrice(discountedItem.discountAmount!);
                                        }
                                        
                                        // Get all discounted items for proportional calculation
                                        const allDiscountedItems = itemsWithDiscounts.filter(item => item.discountAmount && item.discountAmount > 0);
                                        
                                        // Calculate total USD discount across all items
                                        const totalUsdDiscount = allDiscountedItems.reduce((sum, item) => sum + (item.discountAmount || 0), 0);
                                        
                                        // Convert total discount to display currency
                                        const totalConvertedDiscount = getConvertedItemPrice(totalUsdDiscount);
                                        
                                        // Calculate this item's proportion of total USD discount
                                        const itemDiscountProportion = (discountedItem.discountAmount!) / totalUsdDiscount;
                                        
                                        // Find this item's index for "last item gets remainder" logic
                                        const currentItemIndex = allDiscountedItems.findIndex(item => item.id === discountedItem.id);
                                        const isLastItem = currentItemIndex === allDiscountedItems.length - 1;
                                        
                                        if (isLastItem) {
                                          // Last item: get exact remainder to ensure total precision
                                          const otherItemsTotal = allDiscountedItems.slice(0, -1).reduce((sum, otherItem) => {
                                            const otherProportion = (otherItem.discountAmount || 0) / totalUsdDiscount;
                                            return sum + Math.round(totalConvertedDiscount * otherProportion * 100) / 100;
                                          }, 0);
                                          return totalConvertedDiscount - otherItemsTotal;
                                        } else {
                                          // All other items: calculate proportional discount and round
                                          return Math.round(totalConvertedDiscount * itemDiscountProportion * 100) / 100;
                                        }
                                      })();
                                      
                                      // Calculate discounted total mathematically: original - discount
                                      const convertedDiscountedTotal = Math.max(0, originalTotal - convertedDiscountAmount);
                                      
                                      return (
                                        <div className="space-y-1">
                                          <div className="text-sm text-gray-400 line-through">
                                            {formatDisplayAmount(originalTotal)}
                                          </div>
                                          <div className="text-lg font-semibold text-gray-900">
                                            {formatDisplayAmount(convertedDiscountedTotal)}
                                          </div>
                                          <div className="text-xs text-green-600">
                                            -{formatDisplayAmount(convertedDiscountAmount)} discount
                                          </div>
                                        </div>
                                      );
                                    } else {
                                      // Show original total
                                      return (
                                        <div className="text-lg font-semibold text-gray-900">
                                          {formatDisplayAmount(originalTotal)}
                                        </div>
                                      );
                                    }
                                  })()}
                                  <div className="text-xs text-gray-500">{selectedCurrency}</div>
                                </>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Desktop Layout (sm and above) - Redesigned for Better UX */}
                        <div className="hidden sm:block">
                          <div className="bg-white rounded-lg border border-gray-100 p-4 hover:border-gray-200 hover:shadow-sm transition-all duration-200">
                            <div className="flex items-center gap-6">
                              {/* Product Image */}
                              <div className="w-20 h-20 rounded-lg overflow-hidden flex-shrink-0">
                                <img
                                  src={item.image}
                                  alt={item.name}
                                  className="h-full w-full object-cover"
                                />
                              </div>
                              
                              {/* Product Info - Expanded */}
                              <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2 flex-wrap mb-2">
                                  <h3 className="text-lg font-semibold text-gray-900 leading-tight">
                                    <Link
                                      to={`/product/${item.id}`}
                                      className="hover:text-jamaica-green transition-colors"
                                    >
                                      {item.name}
                                    </Link>
                                  </h3>
                                  {hasLimitedShippingOptions(item.id) && (
                                    <div className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 border border-green-200 rounded-md text-xs font-medium">
                                      <Info className="h-3 w-3" />
                                      Express Shipping Required
                                    </div>
                                  )}
                                </div>
                                {item.price > 0 && (
                                  <p className="text-sm text-gray-600">
                                    {item.onSale &&
                                    item.onSalePrice &&
                                    item.onSalePrice < item.price ? (
                                      <>
                                        <span className="text-jamaica-green font-medium">
                                          {formatDisplayAmount(getConvertedItemPrice(item.onSalePrice))} {selectedCurrency}
                                        </span>
                                        <span className="line-through ml-2 text-gray-400">
                                          {formatDisplayAmount(getConvertedItemPrice(item.price))} {selectedCurrency}
                                        </span>
                                        <span className="ml-1 text-xs text-gray-500">per item</span>
                                      </>
                                    ) : (
                                      <>
                                        <span className="font-medium">
                                          {formatDisplayAmount(getConvertedItemPrice(item.price))} {selectedCurrency}
                                        </span>
                                        <span className="ml-1 text-xs text-gray-500">per item</span>
                                      </>
                                    )}
                                  </p>
                                )}
                              </div>
                              
                              {/* Quantity Controls */}
                              {item.price > 0 && (
                                <div className="flex items-center bg-gray-50 rounded-lg border">
                                  <button
                                    onClick={() => handleQuantityChange(item.id, -1)}
                                    disabled={item.quantity <= 1}
                                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-l-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                  >
                                    <Minus className="h-4 w-4" />
                                  </button>
                                  {editingQuantity === item.id ? (
                                    <input
                                      type="text"
                                      value={tempQuantity}
                                      onChange={(e) => handleQuantityInputChange(e.target.value)}
                                      onBlur={() => handleQuantitySubmit(item.id)}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter') {
                                          handleQuantitySubmit(item.id);
                                        } else if (e.key === 'Escape') {
                                          handleQuantityCancel();
                                        }
                                      }}
                                      className="px-4 py-2 font-medium text-gray-900 w-[3rem] text-center bg-transparent border-none outline-none focus:ring-0"
                                      autoFocus
                                    />
                                  ) : (
                                    <span 
                                      className="px-4 py-2 font-medium text-gray-900 min-w-[3rem] text-center cursor-pointer hover:bg-gray-200 rounded transition-colors"
                                      onClick={() => handleQuantityClick(item.id, item.quantity)}
                                    >
                                      {item.quantity}
                                    </span>
                                  )}
                                  <button
                                    onClick={() => handleQuantityChange(item.id, 1)}
                                    className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-r-lg transition-colors"
                                  >
                                    <Plus className="h-4 w-4" />
                                  </button>
                                </div>
                              )}
                              
                              {/* Price Section - Redesigned */}
                              <div className="w-36 text-right">
                                {item.price === 0 ? (
                                  <div className="inline-block px-3 py-1 bg-jamaica-green/10 text-jamaica-green font-bold rounded-full text-sm">
                                    FREE
                                  </div>
                                ) : (
                                  <>
                                    {(() => {
                                      // Get the unit price (sale price if on sale, otherwise regular price)
                                      const unitPriceUsd = item.onSale &&
                                        item.onSalePrice &&
                                        item.onSalePrice < item.price
                                          ? item.onSalePrice
                                          : item.price;
                                      
                                      // Convert unit price to selected currency once
                                      const convertedUnitPrice = getConvertedItemPrice(unitPriceUsd);
                                      
                                      // Calculate original total: converted unit price × quantity
                                      const originalTotal = convertedUnitPrice * item.quantity;
                                      
                                      // Check if this item has a discount applied
                                      const itemsWithDiscounts = appliedDiscount ? applyDiscountToCartItems(appliedDiscount) : cartItems;
                                      const discountedItem = itemsWithDiscounts.find(discountItem => discountItem.id === item.id);
                                      const hasDiscount = discountedItem && discountedItem.discountAmount && discountedItem.discountAmount > 0;
                                      
                                      if (hasDiscount && discountedItem.discountedTotal !== undefined) {
                                        
                                        // Calculate discount amount using proportional distribution in display currency
                                        const convertedDiscountAmount = (() => {
                                          if (!appliedDiscount || selectedCurrency === 'USD') {
                                            // For USD or no discount, use direct conversion
                                            return getConvertedItemPrice(discountedItem.discountAmount!);
                                          }
                                          
                                          // Get all discounted items for proportional calculation
                                          const allDiscountedItems = itemsWithDiscounts.filter(item => item.discountAmount && item.discountAmount > 0);
                                          
                                          // Calculate total USD discount across all items
                                          const totalUsdDiscount = allDiscountedItems.reduce((sum, item) => sum + (item.discountAmount || 0), 0);
                                          
                                          // Convert total discount to display currency
                                          const totalConvertedDiscount = getConvertedItemPrice(totalUsdDiscount);
                                          
                                          // Calculate this item's proportion of total USD discount
                                          const itemDiscountProportion = (discountedItem.discountAmount!) / totalUsdDiscount;
                                          
                                          // Find this item's index for "last item gets remainder" logic
                                          const currentItemIndex = allDiscountedItems.findIndex(item => item.id === discountedItem.id);
                                          const isLastItem = currentItemIndex === allDiscountedItems.length - 1;
                                          
                                          if (isLastItem) {
                                            // Last item: get exact remainder to ensure total precision
                                            const otherItemsTotal = allDiscountedItems.slice(0, -1).reduce((sum, otherItem) => {
                                              const otherProportion = (otherItem.discountAmount || 0) / totalUsdDiscount;
                                              return sum + Math.round(totalConvertedDiscount * otherProportion * 100) / 100;
                                            }, 0);
                                            return totalConvertedDiscount - otherItemsTotal;
                                          } else {
                                            // All other items: calculate proportional discount and round
                                            return Math.round(totalConvertedDiscount * itemDiscountProportion * 100) / 100;
                                          }
                                        })();
                                        
                                        // Calculate discounted total mathematically: original - discount
                                        const convertedDiscountedTotal = Math.max(0, originalTotal - convertedDiscountAmount);
                                        
                                        return (
                                          <div className="space-y-1">
                                            <div className="text-sm text-gray-400 line-through">
                                              {formatDisplayAmount(originalTotal)} {selectedCurrency}
                                            </div>
                                            <div className="text-xl font-bold text-gray-900">
                                              {formatDisplayAmount(convertedDiscountedTotal)} {selectedCurrency}
                                            </div>
                                            <div className="inline-block px-2 py-1 bg-green-50 text-green-700 rounded-full text-xs font-medium">
                                              -{formatDisplayAmount(convertedDiscountAmount)} {selectedCurrency}
                                            </div>
                                          </div>
                                        );
                                      } else {
                                        // Show original total
                                        return (
                                          <div className="text-xl font-bold text-gray-900">
                                            {formatDisplayAmount(originalTotal)} {selectedCurrency}
                                          </div>
                                        );
                                      }
                                    })()}
                                  </>
                                )}
                              </div>
                              
                              {/* Remove Button - Better positioned */}
                              <button
                                onClick={() => handleRemoveItem(item.id)}
                                className="text-gray-400 hover:text-red-500 p-2 rounded-md hover:bg-red-50 transition-colors"
                                title="Remove item"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        </div>

                        <Separator className="my-4" />
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 flex justify-between items-center">
                    <Link
                      to="/products"
                      className="text-jamaica-green hover:text-jamaica-green/80 text-sm font-medium"
                    >
                      ← Continue Shopping
                    </Link>
                    <Button
                      variant="outline"
                      onClick={handleClearCart}
                      className="text-sm border-gray-300 hover:bg-gray-50"
                    >
                      Clear Cart
                    </Button>
                  </div>
                </div>
              </div>

              {/* Order Summary */}
              <div className="lg:col-span-1">
                {/* Cart Recommendations */}
                <CartRecommendations cartProductIds={cartProductIds} />
                
                <div className="relative backdrop-blur-sm bg-white/80 rounded-2xl shadow-xl border border-white/40 p-6 hover:shadow-2xl transition-all duration-500 group">
                  {/* Reward Progress Bar */}
                  <RewardProgressBar cartTotal={usdSubtotal} convertedCartTotal={convertedSubtotal} />
                  {/* Animated Border Accent */}
                  <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-jamaica-green via-jamaica-yellow to-jamaica-green opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-t-2xl"></div>
                  <h3 className="text-xl font-bold text-gray-900 mb-6 pb-2 border-b border-dashed border-gray-200">
                    Order Summary
                  </h3>

                  <div className="space-y-4">
                    <div className="flex justify-between items-center scroll-target">
                      <span className="text-gray-600">Subtotal</span>
                      <span className="font-medium">
                        {formatDisplayAmount(convertedSubtotal)} {selectedCurrency}
                      </span>
                    </div>

                    {/* Shipping Address & Checkout Buttons */}
                    {!isAddressComplete ? (
                      <div className="my-5 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                        <Label className="text-sm font-medium mb-3 text-gray-800 flex items-center">
                          <MapPin className="h-4 w-4 mr-2 text-jamaica-green" />
                          Shipping Address Required
                        </Label>
                        <Button
                          className="w-full bg-jamaica-yellow text-white hover:bg-jamaica-yellow/90 py-4 shadow-md transition-all duration-300 hover:shadow-lg"
                          onClick={() => setShowShippingModal(true)}
                          disabled={
                            checkingAvailability ||
                            loadingItemShipping ||
                            commonShippingOptions.length === 0
                          }
                        >
                          <MapPin className="w-4 h-4 mr-2" />
                          {loadingItemShipping ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Loading Shipping Options...
                            </>
                          ) : commonShippingOptions.length === 0 &&
                            cartItems.length > 0 ? (
                            "No Compatible Shipping Options"
                          ) : (
                            "Add Shipping Address"
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="my-5">
                        {/* Current Address Display */}
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h4 className="text-sm font-medium text-gray-900 mb-1">
                                Shipping to:
                              </h4>
                              <div className="text-sm text-gray-700">
                                <p className="font-medium">{shippingAddress?.value?.name}</p>
                                <p>{shippingAddress?.value?.address?.line1}</p>
                                {shippingAddress?.value?.address?.line2 && (
                                  <p>{shippingAddress?.value?.address?.line2}</p>
                                )}
                                <p>
                                  {shippingAddress?.value?.address?.city}, {shippingAddress?.value?.address?.state} {shippingAddress?.value?.address?.postal_code}
                                </p>
                              </div>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setShowShippingModal(true)}
                              className="text-jamaica-green hover:text-jamaica-green/80"
                            >
                              Change
                            </Button>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Shipping Options */}
                    <div id="shipping-options" className="my-5 bg-gray-50 p-4 rounded-lg">
                      <Label className="text-sm font-medium block mb-3 text-gray-800">
                        Shipping Options
                      </Label>
                      {loadingShippingTiers || loadingItemShipping ? (
                        <div className="text-center py-4">
                          <div className="animate-spin h-4 w-4 border-2 border-jamaica-green border-t-transparent rounded-full mx-auto"></div>
                          <p className="text-sm text-gray-500 mt-2">
                            Loading shipping options...
                          </p>
                        </div>
                      ) : (
                        <>
                          <RadioGroup
                            value={shippingOption}
                            onValueChange={(value) =>
                              setShippingOption(value as ShippingOption)
                            }
                            className="space-y-2 sm:space-y-3"
                          >
                            {(() => {
                              const shippingCountry = shippingAddress?.value?.address?.country;
                              return getAllActiveShippingTiers()
                                .sort((a, b) => a.sortOrder - b.sortOrder)
                                // Show all active shipping tiers, with disabled states for various restrictions
                                .filter((tier) => {
                                  // Always show if we're still loading shipping options
                                  if (loadingItemShipping) {
                                    return true;
                                  }
                                  
                                  // Check if tier is available for all cart items
                                  const isItemCompatible = commonShippingOptions.some(
                                    (option) => option.id === tier.id
                                  );
                                  
                                  // Special handling for express tier - always show if any item supports it
                                  const isExpressTier = tier.id === 'express';
                                  if (isExpressTier) {
                                    // Check if at least one item in cart supports express shipping
                                    const hasExpressCompatibleItem = cartItems.some(item => {
                                      const itemOptions = cartItemsShippingOptions[item.id] || [];
                                      return itemOptions.includes('express');
                                    });
                                    
                                    const noShippingRestrictionsLoaded = Object.keys(cartItemsShippingOptions).length === 0;
                                    
                                    // Show express if any item supports it OR if no specific restrictions are loaded
                                    return hasExpressCompatibleItem || noShippingRestrictionsLoaded;
                                  }
                                  
                                  // Special handling for standard tier - always show (may be disabled for various reasons)
                                  const isStandardTier = tier.id === 'standard';
                                  if (isStandardTier) {
                                    // Always show standard tier
                                    return true;
                                  }
                                  
                                  // For other tiers, always show them (they may be disabled in rendering)
                                  // This ensures users see all shipping options with appropriate disabled states
                                  return true;
                                });
                            })()
                              .map((tier) => {
                                // Helper function to get effective cost for the tier
                                const getEffectiveCost = () => {
                                  // If no address is selected, return null for ALL tiers to show "Address Required"
                                  if (!isAddressComplete) {
                                    return null;
                                  }
                                  const baseTierCost = isJamaicaAddress && tier.jmPrice != null ? tier.jmPrice : tier.cost;
                                  
                                  // Check for free shipping threshold first with display currency precision
                                  if (tier.freeShippingThreshold && tier.freeShippingThreshold > 0) {
                                    // Use display currency precision for threshold comparison (same logic as milestone detection)
                                    const isFreeShippingEarned = selectedCurrency === 'USD' 
                                      ? usdSubtotal >= tier.freeShippingThreshold
                                      : convertedSubtotal >= getConvertedItemPrice(tier.freeShippingThreshold);
                                    
                                    if (isFreeShippingEarned) {
                                      return 0;
                                    }
                                  }
                                  
                                  // Special handling for express tier with final express rate
                                  if (tier.id === 'express') {
                                    // Use final express rate (already has express discount applied) or fall back to tier cost
                                    return finalExpressRate !== null ? finalExpressRate : baseTierCost;
                                  }
                                  
                                  // Fallback to tier's configured cost for all other tiers
                                  return baseTierCost;
                                };

                                const cost = getEffectiveCost();
                                const isSelected = shippingOption === tier.id;
                                
                                // Shipping tier states
                                const isExpressTier = tier.id === 'express';
                                const isStandardTier = tier.id === 'standard';
                                const isExpressLoading = isExpressTier && (loadingDhlRate || isDebouncingShipping);
                                
                                // Determine if tier should be disabled for various reasons
                                const isDisabledDueToAddress = cost === null; // Disabled when no address (applies to all tiers)
                                const isDisabledDueToIncompatibility = isStandardTier && shouldStandardShippingBeDisabled();
                                const isDisabledDueToTerritory = isTierDisabledDueToTerritory(tier, shippingAddress?.value?.address?.country);
                                const isDisabled = isDisabledDueToAddress || isDisabledDueToIncompatibility || isDisabledDueToTerritory;

                                return (
                                  <div key={tier.id} className="space-y-2">
                                    <div
                                      className={`grid grid-cols-[auto_1fr_auto] gap-2 items-center rounded-md border p-3 sm:p-4 transition-all duration-200 ${
                                        isDisabled 
                                          ? "opacity-60 cursor-not-allowed border-gray-200 bg-gray-50"
                                          : `hover:scale-[1.01] active:scale-[0.99] cursor-pointer ${
                                              isSelected
                                                ? "border-jamaica-green bg-jamaica-green/5 shadow-md"
                                                : "border-gray-200 hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm"
                                            }`
                                      }`}
                                      onClick={() => {
                                        if (!isDisabled) {
                                          setShippingOption(tier.id as ShippingOption);
                                        }
                                      }}
                                    >
                                    {/* Radio Button - Fixed Position */}
                                    <RadioGroupItem
                                      value={tier.id}
                                      id={`${tier.id}-shipping`}
                                      className="text-jamaica-green"
                                      disabled={isDisabled}
                                    />
                                    
                                    {/* Content Area - Flexible */}
                                    <div className="cursor-pointer min-w-0">
                                      <div className="font-medium text-sm leading-tight text-gray-800">
                                        {tier.name}
                                      </div>
                                      <div className="text-xs text-gray-500 mt-0.5 leading-tight">
                                        {tier.deliveryTimeMin === tier.deliveryTimeMax
                                          ? `${tier.deliveryTimeMin} business day${tier.deliveryTimeMin !== 1 ? "s" : ""}`
                                          : `${tier.deliveryTimeMin}-${tier.deliveryTimeMax} business days after order processing`}
                                      </div>
                                      
                                      
                                    </div>
                                    
                                    {/* Price Area - Fixed Width */}
                                    <div className={`text-right min-w-[80px] sm:min-w-[100px] ${
                                      cost === 0
                                        ? "text-jamaica-green"
                                        : cost === null
                                        ? "text-amber-600"
                                        : "text-gray-900"
                                    }`}>
                                      {(isExpressLoading || isDebouncingShipping) ? (
                                        <div className="flex items-center justify-end">
                                          <Loader2 className="h-3 w-3 animate-spin mr-1" />
                                          <div className="text-xs text-gray-500 leading-tight">
                                            {isExpressLoading ? 'Getting' : 'Updating'}<br className="sm:hidden" />
                                            <span className="hidden sm:inline"> </span>rate...
                                          </div>
                                        </div>
                                      ) : cost === null ? (
                                        <div className="text-center">
                                          <div className="text-xs font-medium text-amber-600 leading-tight">
                                            Address<br className="sm:hidden" />
                                            <span className="hidden sm:inline"> </span>Required
                                          </div>
                                        </div>
                                      ) : cost === 0 ? (
                                        <div className="font-semibold text-jamaica-green text-sm">FREE</div>
                                      ) : (
                                        <div>
                                          {/* Special pricing display for Rush'd members on Express tier */}
                                          {isExpressTier && isMember && isActive && rushdPricingQuote?.isEligible && 
                                           !loadingFinalExpressRate && discountLoaded && !loadingRushdPricing && 
                                           appliedDiscount?.discount.type !== 'free_shipping' ? (
                                            <div className="space-y-1">
                                              {/* Original Express rate crossed out */}
                                              <div className="text-xs text-gray-400 leading-tight line-through">
                                                {formatDisplayAmount(getConvertedItemPrice(finalExpressRate || 0))}
                                              </div>
                                              {/* Rush'd member price */}
                                              <div className="font-semibold text-sm sm:text-base leading-tight text-jamaica-green">
                                                {formatDisplayAmount(getConvertedItemPrice(calculatedMemberRate))}
                                              </div>
                                              <div className="text-xs text-gray-500 leading-tight">
                                                {selectedCurrency}
                                              </div>
                                            </div>
                                          ) : (
                                            /* Regular pricing display */
                                            <>
                                              <div className="font-semibold text-sm sm:text-base leading-tight">
                                                {formatDisplayAmount(getConvertedItemPrice(cost))}
                                              </div>
                                              <div className="text-xs text-gray-500 leading-tight">
                                                {selectedCurrency}
                                              </div>
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                    </div>
                                    
                                    {/* Explanatory notes for disabled shipping - territory restrictions take priority */}
                                    {isDisabledDueToTerritory && shippingAddress?.value?.address?.country ? (
                                      <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-md">
                                        <div className="flex items-start gap-2">
                                          <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                          <div className="text-xs text-amber-800">
                                            <p className="font-medium mb-1">{tier.name} not available</p>
                                            <p>{tier.name} is not available for delivery to the country of your selected address.</p>
                                          </div>
                                        </div>
                                      </div>
                                    ) : isDisabledDueToIncompatibility && (
                                      <div className="px-3 py-2 bg-amber-50 border border-amber-200 rounded-md">
                                        <div className="flex items-start gap-2">
                                          <Info className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                                          <div className="text-xs text-amber-800">
                                            <p className="font-medium mb-1">{tier.name} unavailable</p>
                                            <p>Your cart contains {getExpressOnlyItems().length === 1 ? 'an item' : 'items'} that require express shipping. Remove {getExpressOnlyItems().length === 1 ? 'this item' : 'these items'} to enable {tier.name.toLowerCase()}.</p>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                          </RadioGroup>

                          {/* Rush'd Member Savings Notice - below Express option */}
                          {availableShippingTiers.some(tier => tier.id === 'express') && 
                           !loadingFinalExpressRate && 
                           !loadingDhlRate &&
                           !isDebouncingShipping &&
                           discountLoaded && 
                           !loadingRushdPricing &&
                           !rushdSettingsLoading &&
                           calculatedMemberRate > 0 && (
                            <div className="mt-3">
                              {(() => {
                                // Get the Express tier to calculate regular vs Rush'd pricing
                                const expressTier = availableShippingTiers.find(tier => tier.id === 'express');
                                if (!expressTier) return null;

                                // Calculate the actual express cost that the user pays (including free shipping threshold)
                                const getActualExpressCost = () => {
                                  if (!expressTier) return null;
                                  
                                  // Check for free shipping threshold first (same logic as shipping options)
                                  if (expressTier.freeShippingThreshold && expressTier.freeShippingThreshold > 0) {
                                    const isFreeShippingEarned = selectedCurrency === 'USD' 
                                      ? usdSubtotal >= expressTier.freeShippingThreshold
                                      : convertedSubtotal >= getConvertedItemPrice(expressTier.freeShippingThreshold);
                                    
                                    if (isFreeShippingEarned) {
                                      return 0; // Express is free due to threshold
                                    }
                                  }
                                  
                                  // Otherwise use the final express rate
                                  return finalExpressRate;
                                };
                                
                                const regularExpressRate = getActualExpressCost();
                                if (regularExpressRate === null) return null;

                                // Use dynamically calculated member rate
                                const memberRate = calculatedMemberRate;

                                // Convert base USD amounts to display currency first
                                const convertedRegularRate = getConvertedItemPrice(regularExpressRate);
                                const convertedMemberRate = getConvertedItemPrice(memberRate);
                                
                                // Calculate savings in display currency for mathematical consistency
                                const convertedSavings = convertedRegularRate - convertedMemberRate;


                                const isActiveMember = isMember && isActive && rushdPricingQuote?.isEligible && appliedDiscount?.discount.type !== 'free_shipping';

                                if (isActiveMember && convertedSavings > 0 && isAddressComplete) {
                                  return (
                                    <div className="bg-gradient-to-r from-green-50 to-green-50/50 border border-green-200 rounded-lg p-3 shadow-sm">
                                      <div className="flex items-center gap-2 text-sm text-green-800">
                                        <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                                        <span>
                                          You saved <span className="font-bold text-green-700">{formatDisplayAmount(convertedSavings)}</span> on Express shipping with{' '}
                                          <Link to="/rushd/pricing" className="text-green-800 hover:text-green-700 font-semibold underline decoration-green-300">
                                            JamPack'd Rush'd
                                          </Link>
                                          !
                                        </span>
                                      </div>
                                    </div>
                                  );
                                }

                                const shouldShowRecruitCard = isAddressComplete && rushdSettings?.programActive && !isActiveMember;
                                if (!shouldShowRecruitCard) {
                                  return null;
                                }

                                if (regularExpressRate > 0 && memberRate > 0 && convertedSavings > 0) {
                                  return (
                                    <div className="relative overflow-hidden bg-gradient-to-r from-jamaica-green/8 to-jamaica-yellow/8 border-2 border-jamaica-green/30 rounded-lg p-3 shadow-lg animate-in fade-in slide-in-from-top-2 duration-500 hover:scale-[1.02] hover:shadow-xl transition-all">
                                      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-[shimmer_3s_ease-in-out_infinite]" style={{ backgroundSize: '200% 100%' }} />
                                      <div className="relative flex items-start gap-2">
                                        <Tag className="w-4 h-4 text-jamaica-green flex-shrink-0 mt-0.5 animate-pulse" />
                                        <div className="flex-1">
                                          <div className="text-sm text-gray-800 leading-relaxed">
                                            Pay only <span className="font-bold text-jamaica-green animate-pulse">{formatDisplayAmount(convertedMemberRate)}</span> for Express shipping with{' '}
                                            <span className="inline-flex items-center gap-1">
                                              <Crown className="w-3 h-3 text-jamaica-yellow animate-bounce" />
                                              <Link to="/rushd/pricing" className="text-jamaica-green hover:text-jamaica-green/80 font-semibold underline decoration-jamaica-green/30 transition-colors">
                                                JamPack'd Rush'd
                                              </Link>
                                            </span>
                                            {' '}and save <span className="font-bold text-jamaica-green animate-pulse">{formatDisplayAmount(convertedSavings)}</span>
                                          </div>
                                          <Link 
                                            to="/rushd/pricing" 
                                            className="inline-flex items-center gap-1 text-xs text-jamaica-green hover:text-jamaica-green/80 font-medium mt-1 group transition-all hover:gap-2"
                                          >
                                            Click here to learn more 
                                            <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-1" />
                                          </Link>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                }

                                return (
                                  <div className="bg-gradient-to-r from-jamaica-green/5 to-jamaica-yellow/5 border border-jamaica-green/15 rounded-lg p-3">
                                    <div className="flex items-center gap-2">
                                      <Crown className="w-4 h-4 text-jamaica-yellow flex-shrink-0" />
                                      <div className="flex-1">
                                        <div className="text-sm text-gray-700">
                                          Get discounted Express shipping rates with JamPack'd Rush'd membership
                                        </div>
                                        <Link 
                                          to="/rushd/pricing" 
                                          className="inline-flex items-center gap-1 text-xs text-jamaica-green hover:text-jamaica-green/80 font-medium mt-1 group"
                                        >
                                          Click here to learn more
                                          <ArrowRight className="w-3 h-3 transition-transform group-hover:translate-x-0.5" />
                                        </Link>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })()}
                            </div>
                          )}
                        </>
                      )}

                      {!loadingShippingTiers &&
                        !loadingItemShipping &&
                        commonShippingOptions.length === 0 &&
                        cartItems.length > 0 && (
                          <div className="text-center py-4 px-3 bg-amber-50 border border-amber-200 rounded-lg">
                            <Info className="h-5 w-5 text-amber-600 mx-auto mb-2" />
                            <p className="text-sm text-amber-800 font-medium mb-1">
                              No Common Shipping Options Available
                            </p>
                            <p className="text-xs text-amber-700">
                              The items in your cart have different shipping
                              restrictions. Please review your cart items or
                              contact support for assistance.
                            </p>
                          </div>
                        )}
                    </div>


                    {shippingOption && availableShippingTiers.some(tier => tier.id === shippingOption) && isAddressComplete && (
                      <div className="flex justify-between items-center">
                        <div className="flex items-center">
                          <span className="text-gray-600">Shipping Cost</span>
                        </div>
                        <span className="font-medium">
                          {(shippingOption === 'express' && (loadingDhlRate || !discountLoaded || isDebouncingShipping)) ? (
                            <div className="flex items-center space-x-2">
                              <div className="animate-spin h-3 w-3 border border-jamaica-green border-t-transparent rounded-full"></div>
                              <span className="text-gray-500 text-sm">Calculating...</span>
                            </div>
                          ) : (
                            <>
                              {formatDisplayAmount(convertedShipping)} {selectedCurrency}
                            </>
                          )}
                        </span>
                      </div>
                    )}

                    {priorityProcessing && isAddressComplete && (
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">
                          Priority Processing
                        </span>
                        <span className="font-medium">
                          {isMember && isActive 
                            ? <span className="text-green-600">Included</span>
                            : `${formatDisplayAmount(convertedPriorityFee)} ${selectedCurrency}`
                          }
                        </span>
                      </div>
                    )}

                    {appliedDiscount && appliedDiscount.discount.type === "free_shipping" && isAddressComplete && (
                      <div className="flex justify-between text-green-600 items-center">
                        <span className="flex items-center">
                          <Check className="h-4 w-4 mr-1" />
                          Free Shipping
                        </span>
                        <span className="font-medium">
                          -{formatDisplayAmount(convertedBaseShippingCost)} {selectedCurrency}
                        </span>
                      </div>
                    )}

                    <Separator className="my-2" />

                    <div className="flex justify-between items-center py-2">
                      <span className="font-semibold text-gray-800">Total</span>
                      <div className="text-right">
                        {!isAddressComplete ? (
                          <div className="text-center">
                            <div className="text-sm font-medium text-amber-600 leading-tight">
                              Address Required<br />for Total Calculation
                            </div>
                          </div>
                        ) : (shippingOption === 'express' && (loadingDhlRate || !discountLoaded || isDebouncingShipping)) ? (
                          <div className="flex items-center space-x-2">
                            <div className="animate-spin h-3 w-3 border border-jamaica-green border-t-transparent rounded-full"></div>
                            <span className="text-gray-500 text-sm">Calculating...</span>
                          </div>
                        ) : (
                          <>
                            <span className="font-bold text-xl text-jamaica-green">
                              {formatDisplayAmount(convertedTotal)}
                            </span>
                            <span className="text-xs text-gray-500 block">{selectedCurrency}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Priority Processing Option */}
                  <div className="mt-6 mb-4 bg-gray-50 p-4 rounded-lg">
                    <div className="flex items-center justify-between space-x-2">
                      <div>
                        <Label
                          htmlFor="priority-processing"
                          className="text-sm font-medium text-gray-800"
                        >
                          Priority Processing
                        </Label>
                        <p className="text-xs text-gray-500 mt-1">
                          Your order will be prioritized and packed ahead of normal orders
                          {isMember && isActive 
                            ? " (Included with Rush'd membership)"
                            : ` (+${formatDisplayAmount(getConvertedItemPrice(3.00))} ${selectedCurrency})`
                          }
                        </p>
                      </div>
                      <Switch
                        id="priority-processing"
                        checked={priorityProcessing}
                        onCheckedChange={setPriorityProcessing}
                        disabled={isMember && isActive}
                        className="data-[state=checked]:bg-jamaica-green"
                      />
                    </div>
                  </div>

                  {/* Promo Code Field */}
                  <div className="mt-6 mb-6">
                    {!appliedDiscount && (
                      <Label
                        htmlFor="promo-code"
                        className="text-sm font-medium mb-2 block text-gray-800"
                      >
                        Have a discount code? Enter it below:
                      </Label>
                    )}

                    {appliedDiscount ? (
                      // Applied discount display
                      <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
                            <Check className="h-4 w-4 text-green-600" />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-medium text-green-800">
                                {appliedDiscount.discount.code}
                              </span>
                              <span className="text-sm text-green-700">
                                -
                                {appliedDiscount.discount.type === "percentage"
                                  ? `${appliedDiscount.discount.value}%`
                                  : appliedDiscount.discount.type === "fixed"
                                  ? formatDisplayAmount(getConvertedItemPrice(appliedDiscount.discount.value))
                                  : "Free Shipping"}
                              </span>
                            </div>
                            <p className="text-xs text-green-600 mt-1">
                              {appliedDiscount.discount.type === "free_shipping"
                                ? `You saved ${formatDisplayAmount(convertedBaseShippingCost)} ${selectedCurrency}`
                                : "Discount applied to individual items"}
                            </p>
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={handleRemovePromo}
                          className="h-8 w-8 p-0 text-green-600 hover:text-green-700 hover:bg-green-100"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      // Promo code input
                      <div className="flex mt-1">
                        <Input
                          id="promo-code"
                          type="text"
                          placeholder="Enter discount code"
                          className="rounded-r-none focus-visible:ring-0 focus-visible:ring-offset-0 focus:border-gray-300"
                          value={promoCode}
                          onChange={(e) =>
                            setPromoCode(e.target.value.toUpperCase())
                          }
                          disabled={isValidatingPromo}
                          onKeyPress={(e) => {
                            if (e.key === "Enter") {
                              e.preventDefault();
                              handleApplyPromo();
                            }
                          }}
                        />
                        <Button
                          type="button"
                          className="rounded-l-none bg-jamaica-green hover:bg-jamaica-green/90"
                          disabled={isValidatingPromo || !promoCode.trim()}
                          onClick={(e) => {
                            e.preventDefault();
                            handleApplyPromo();
                          }}
                        >
                          {isValidatingPromo ? (
                            <>
                              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                              Applying...
                            </>
                          ) : (
                            "Apply"
                          )}
                        </Button>
                      </div>
                    )}
                  </div>

                  {/* Checkout Button */}
                  <Button
                    className="w-full mt-2 bg-jamaica-green text-white hover:bg-jamaica-green/90 py-6 shadow-md transition-all duration-300 hover:shadow-lg disabled:bg-gray-400 disabled:cursor-not-allowed"
                    onClick={isAddressComplete ? handleCheckout : () => setShowShippingModal(true)}
                    disabled={
                      checkingAvailability ||
                      loadingItemShipping ||
                      commonShippingOptions.length === 0 ||
                      // Prevent proceeding while express rate UI is still resolving
                      (shippingOption === 'express' && (loadingDhlRate || loadingFinalExpressRate || loadingRushdPricing))
                    }
                  >
                    {checkingAvailability ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Checking Availability...
                      </>
                    ) : loadingItemShipping ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Loading Shipping Options...
                      </>
                    ) : (shippingOption === 'express' && (loadingDhlRate || loadingFinalExpressRate || loadingRushdPricing)) ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Getting Shipping Rate...
                      </>
                    ) : commonShippingOptions.length === 0 &&
                      cartItems.length > 0 ? (
                      "No Compatible Shipping Options"
                    ) : !isAddressComplete ? (
                      <>
                        Add Shipping Address
                      </>
                    ) : (
                      "Proceed to Checkout"
                    )}
                  </Button>

                  {/* Dynamic Shipping Policy */}
                  {availableShippingTiers.length > 0 && (
                    <p className="text-xs text-gray-500 mt-4 text-center">
                      {(() => {
                        // Get shipping country from address
                        const shippingCountry = shippingAddress?.value?.address?.country;
                        
                        // Get all active tiers sorted by Firebase configuration order (sortOrder)
                        const allTiers = getAllActiveShippingTiers();
                        
                        // Find the first tier in configuration order that:
                        // 1. Is available in user's country (not disabled due to territory)
                        // 2. Has a free shipping threshold
                        for (const tier of allTiers) {
                          // Check if tier is available in user's country
                          const isAvailableInCountry = !isTierDisabledDueToTerritory(tier, shippingCountry);
                          
                          // Check if tier has free shipping threshold
                          const hasFreeShippingThreshold = tier.freeShippingThreshold && tier.freeShippingThreshold > 0;
                          
                          if (isAvailableInCountry && hasFreeShippingThreshold) {
                            return `Free ${tier.name.toLowerCase()} on orders ${formatDisplayAmount(getConvertedItemPrice(tier.freeShippingThreshold!))} and above.`;
                          }
                        }
                        
                        return "";
                      })()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Scroll Down FAB - Only visible when at top of page and cart is not empty */}
      {getCartCount() > 0 && (
        <ScrollDownFAB 
          scrollTarget="#shipping-options" 
          tooltipText="Scroll to shipping options"
          className="!bottom-24 sm:!bottom-8"
        />
      )}

      {/* Shipping Address Modal */}
      <ShippingAddressModal
        open={showShippingModal}
        onOpenChange={setShowShippingModal}
        onAddressConfirmed={handleShippingAddressConfirmed}
        currentAddress={shippingAddress}
      />
    </div>
    </>
  );
};

export default Cart;
